// Sentry event policy for the web app: what never reaches Sentry (`shouldDropEvent`) and what is
// stripped from everything that does (`scrubEvent` / `scrubConsoleBreadcrumb`).
//
// RxDB appends its error `parameters` — which for write/validation/replication
// errors include the full failing document — to RxError.message after this marker.
// Anything logged or sent to Sentry must be truncated there (HIPAA).
// The format comes from `messageForError`/`parametersToString` in rxdb's rx-error.ts
// (verified against rxdb 16.21.0 and 17.3.0): the marker is the 20-hyphen separator
// line followed by `Parameters:`.
const RX_ERROR_PARAMETERS_MARKER = '\n--------------------\nParameters:';

export const stripRxErrorParameters = (message: string): string => {
  const markerIndex = message.indexOf(RX_ERROR_PARAMETERS_MARKER);
  return markerIndex === -1 ? message : `${message.slice(0, markerIndex)} [parameters stripped]`;
};

export type ConsoleBreadcrumbLike = {
  category?: string;
  message?: string;
  data?: { arguments?: unknown[] } & Record<string, unknown>;
};

// Applied at both Sentry boundaries (beforeSend breadcrumbs and Replay
// beforeAddRecordingEvent) so raw console.error(rxError) calls never ship the
// embedded document, while useful primitives (ids, counts) survive.
export const scrubConsoleBreadcrumb = (entry: ConsoleBreadcrumbLike): void => {
  if (entry.message) entry.message = stripRxErrorParameters(entry.message);
  if (entry.category === 'console' && entry.data?.arguments) {
    entry.data.arguments = entry.data.arguments.map(scrubArgument);
  }
};

// Single PHI-scrub entry point for `beforeSend`: strips the RxDB document
// payload from exception values, console breadcrumbs, and captureConsoleIntegration
// events (tagged `logger: 'console'`, raw console args in `extra.arguments`).
// The generic preserves the concrete event type (beforeSend must return ErrorEvent).
export const scrubEvent = <T extends SentryEventLike>(event: T): T => {
  event.exception?.values?.forEach((ex) => {
    if (ex.value) ex.value = stripRxErrorParameters(ex.value);
  });
  event.breadcrumbs?.forEach(scrubConsoleBreadcrumb);

  if (event.logger !== 'console') return event;

  if (typeof event.message === 'string') event.message = stripRxErrorParameters(event.message);
  if (Array.isArray(event.extra?.arguments)) {
    event.extra.arguments = event.extra.arguments.map(scrubArgument);
  }

  return event;
};

// Single drop entry point for `beforeSend`, kept beside the scrub so event policy lives in one
// module (`instrument.ts` is a side-effect `Sentry.init` call and cannot be unit tested).
// Firebase owns the cancelled-request log, so it cannot be downgraded at the call site the way our
// own console.error calls are, and cancelled requests are routine. Every other Firebase error ships.
export const shouldDropEvent = (event: SentryEventLike): boolean =>
  (event.exception?.values ?? []).some((ex) => ex.type === 'FirebaseError' && ex.value === 'Request was cancelled.');

type SentryEventLike = {
  logger?: string;
  message?: string;
  extra?: { arguments?: unknown[] } & Record<string, unknown>;
  exception?: { values?: { value?: string; type?: string }[] };
  breadcrumbs?: ConsoleBreadcrumbLike[];
};

const scrubArgument = (arg: unknown): unknown => {
  if (typeof arg === 'string') return stripRxErrorParameters(arg);
  if (arg && typeof arg === 'object') {
    const record = arg as Record<string, unknown>;
    const scrubbed: Record<string, unknown> = { ...record };
    delete scrubbed.parameters;
    if (typeof scrubbed.message === 'string') scrubbed.message = stripRxErrorParameters(scrubbed.message);
    if (typeof scrubbed.stack === 'string') scrubbed.stack = stripRxErrorParameters(scrubbed.stack);
    return scrubbed;
  }
  return arg;
};
